import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Extension } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";
import { ReactRenderer } from "@tiptap/react";
import tippy from "tippy.js";
import { Heading1, Heading2, Heading3, List, ListOrdered, CheckSquare, Code, ImageIcon, Minus, Quote } from "lucide-react";

// 1. Component for the floating menu list
export const MenuList = forwardRef((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = index => {
    const item = props.items[index];
    if (item) {
      props.command(item);
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }
      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }
      if (event.key === "Enter") {
        enterHandler();
        return true;
      }
      return false;
    }
  }));

  if (!props.items.length) return null;

  return (
    <div style={{
      background: "hsl(var(--background))",
      border: "1px solid hsl(var(--border))",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      padding: "4px",
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      width: "240px",
      maxHeight: "300px",
      overflowY: "auto"
    }}>
      {props.items.map((item, index) => (
        <button
          key={index}
          onClick={() => selectItem(index)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px",
            background: index === selectedIndex ? "hsl(var(--accent))" : "transparent",
            color: "hsl(var(--foreground))",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            textAlign: "left",
            width: "100%"
          }}
        >
          {item.icon && <item.icon size={16} />}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: "14px", fontWeight: "600" }}>{item.title}</span>
            <span style={{ fontSize: "12px", color: "hsl(var(--muted-foreground))" }}>{item.description}</span>
          </div>
        </button>
      ))}
    </div>
  );
});

// 2. The Items List
const getSuggestionItems = ({ query }) => {
  return [
    { title: "Titre 1", description: "Grand titre de section", icon: Heading1, command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode("heading", { level: 1 }).run() },
    { title: "Titre 2", description: "Titre de sous-section", icon: Heading2, command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode("heading", { level: 2 }).run() },
    { title: "Titre 3", description: "Titre de sous-sous-section", icon: Heading3, command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setNode("heading", { level: 3 }).run() },
    { title: "Liste à puces", description: "Créer une liste simple", icon: List, command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleBulletList().run() },
    { title: "Liste numérotée", description: "Créer une liste numérotée", icon: ListOrdered, command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleOrderedList().run() },
    { title: "Liste de tâches", description: "Créer une liste de tâches", icon: CheckSquare, command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleTaskList().run() },
    { title: "Citation", description: "Insérer une citation", icon: Quote, command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleBlockquote().run() },
    { title: "Code", description: "Insérer un bloc de code", icon: Code, command: ({ editor, range }) => editor.chain().focus().deleteRange(range).toggleCodeBlock().run() },
    { title: "Séparateur", description: "Insérer une ligne horizontale", icon: Minus, command: ({ editor, range }) => editor.chain().focus().deleteRange(range).setHorizontalRule().run() },
    { title: "Image", description: "Insérer une image via URL", icon: ImageIcon, command: ({ editor, range }) => {
        const url = window.prompt("URL de l'image :");
        if (url) editor.chain().focus().deleteRange(range).setImage({ src: url }).run();
        else editor.chain().focus().deleteRange(range).run();
    }},
  ].filter(item => item.title.toLowerCase().startsWith(query.toLowerCase())).slice(0, 10);
};

// 3. TipTap Extension
export const SlashMenuExtension = Extension.create({
  name: "slashMenu",
  addOptions() {
    return {
      suggestion: {
        char: "/",
        command: ({ editor, range, props }) => {
          props.command({ editor, range });
        },
      },
    };
  },
  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});

// 4. Suggestion Config object
export const slashSuggestionConfig = {
  items: getSuggestionItems,
  render: () => {
    let component;
    let popup;

    return {
      onStart: props => {
        component = new ReactRenderer(MenuList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) return;

        popup = tippy("body", {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },
      onUpdate(props) {
        component.updateProps(props);

        if (!props.clientRect) return;

        popup[0].setProps({
          getReferenceClientRect: props.clientRect,
        });
      },
      onKeyDown(props) {
        if (props.event.key === "Escape") {
          popup[0].hide();
          return true;
        }
        return component.ref?.onKeyDown(props);
      },
      onExit() {
        popup[0].destroy();
        component.destroy();
      },
    };
  },
};
